/**
 * 家庭时区时间工具（见方案 3.5）。
 *
 * 为什么必须集中实现：服务器时区是 Asia/Tokyo，家庭时区是 Asia/Shanghai；
 * 所有"今天""当日 24:00""每天 0 点生成实例"都必须按**家庭时区**计算，
 * 否则会出现"孩子在 23:30 打卡却算到第二天"这类无法解释的错误。
 *
 * 全部基于 Intl API，不引入 dayjs/luxon 等额外依赖。
 */
import { DEFAULT_FAMILY_TIMEZONE } from './constants.js';

/** 一天的毫秒数。 */
export const DAY_MS = 86_400_000;

/** 把某一时刻格式化为家庭时区下的 'YYYY-MM-DD'。 */
export function toLocalDateString(date: Date, timeZone: string = DEFAULT_FAMILY_TIMEZONE): string {
  // en-CA 的短日期格式恰好是 YYYY-MM-DD，避免手工拼接
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** 家庭时区下的 'HH:mm:ss'。 */
export function toLocalTimeString(date: Date, timeZone: string = DEFAULT_FAMILY_TIMEZONE): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * 求某时刻在家庭时区下的 UTC 偏移（分钟）。
 * 做法：把该时刻在目标时区的"墙上时间"重新按 UTC 解释，差值即偏移。
 */
export function timeZoneOffsetMinutes(date: Date, timeZone: string = DEFAULT_FAMILY_TIMEZONE): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  return Math.round((asUtc - date.getTime()) / 60_000);
}

/**
 * 严格解析 'YYYY-MM-DD'。
 *
 * 为什么不能只靠 Date.UTC：`Date.UTC(2026, 12, 1)` 会**静默滚动**到 2027-01-01，
 * 于是 month=13 这种非法输入会被算成一个合法日期，把错误藏到统计数字里。
 * 因此这里显式校验取值范围，并做一次往返一致性检查（抓出 2 月 30 日这类不存在的日期）。
 */
function parseLocalDate(localDate: string): { y: number; m: number; d: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
  if (!match) throw new Error(`非法日期格式（应为 YYYY-MM-DD）: ${localDate}`);

  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);

  if (m < 1 || m > 12) throw new Error(`非法月份: ${localDate}`);
  if (d < 1 || d > 31) throw new Error(`非法日号: ${localDate}`);

  // 往返校验：不存在的日期（如 2026-02-30）会被 Date 滚动，必须拒绝
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (
    probe.getUTCFullYear() !== y ||
    probe.getUTCMonth() !== m - 1 ||
    probe.getUTCDate() !== d
  ) {
    throw new Error(`日期不存在: ${localDate}`);
  }

  return { y, m, d };
}

/**
 * 严格解析 'HH:mm' 或 'HH:mm:ss'。
 *
 * 接受两种写法是因为来源不同：`node.due_time` 由 PostgreSQL 的 `time` 类型读出，
 * 形状是 `'19:00:00'`；而前端表单/配置里人手写的是 `'19:00'`。
 * 与 `parseLocalDate` 同样的理由：非法值必须在这里立刻报错，
 * 不能让它悄悄滚成"次日 00:05"这种看起来正常却错一天的截止时刻。
 */
function parseLocalTime(localTime: string): { h: number; mi: number; s: number } {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localTime.trim());
  if (!match) throw new Error(`非法时刻格式（应为 HH:mm 或 HH:mm:ss）: ${localTime}`);

  const h = Number(match[1]);
  const mi = Number(match[2]);
  const s = Number(match[3] ?? '0');

  if (h > 23) throw new Error(`非法小时: ${localTime}`);
  if (mi > 59) throw new Error(`非法分钟: ${localTime}`);
  if (s > 59) throw new Error(`非法秒: ${localTime}`);

  return { h, mi, s };
}

/**
 * 把家庭时区下的某个**墙上时刻**（日期 'YYYY-MM-DD' + 时间 'HH:mm[:ss]'）
 * 对应的 UTC 时刻求出来。
 *
 * 这是"日期 + 时刻"类字段的唯一换算入口：`localDayStartUtc` 只是它取 `00:00:00`
 * 的特例，作业的截止时刻（`node.due_time` → `checkin.due_at`）也走它。
 * 全部复用同一段"猜一次、按偏移修正一次"的逻辑 —— 不要在调用方手写 UTC 偏移。
 */
export function localDateTimeUtc(
  localDate: string,
  localTime: string,
  timeZone: string = DEFAULT_FAMILY_TIMEZONE,
): Date {
  const { y, m, d } = parseLocalDate(localDate);
  const { h, mi, s } = parseLocalTime(localTime);
  // 先用 UTC 猜一个时刻，再按偏移修正一次（对夏令时也够用，家庭时区无 DST 更无问题）
  const guess = Date.UTC(y, m - 1, d, h, mi, s);
  const offset1 = timeZoneOffsetMinutes(new Date(guess), timeZone);
  const candidate = guess - offset1 * 60_000;
  const offset2 = timeZoneOffsetMinutes(new Date(candidate), timeZone);
  return new Date(guess - offset2 * 60_000);
}

/**
 * 把家庭时区下的某个日期（'YYYY-MM-DD'）在当天 00:00:00 对应的 UTC 时刻求出来。
 * 用于生成"当日 0 点"的打卡记录与结算边界。
 */
export function localDayStartUtc(
  localDate: string,
  timeZone: string = DEFAULT_FAMILY_TIMEZONE,
): Date {
  return localDateTimeUtc(localDate, '00:00:00', timeZone);
}

/**
 * 把 'HH:mm:ss' / 'HH:mm' 规范成 'HH:mm'（分钟精度，家庭场景够用）。
 *
 * 为什么需要它：PG 的 `time` 列读出来是 `'19:00:00'`，而前端表单与人写的是 `'19:00'`。
 * 若把两种形状都塞进响应，前端就得各自处理两遍；统一在这里收敛成一种。
 * 无法识别的值返回 null（调用方按"没填"处理，而不是把一个坏字符串传给界面）。
 */
export function formatTimeOfDay(time: string | null | undefined): string | null {
  if (!time) return null;
  const match = /^(\d{2}:\d{2})/.exec(time.trim());
  return match ? match[1]! : null;
}

/** 家庭时区下的当天结束时刻（次日 00:00，用于"当日结算"边界）。 */
export function localDayEndUtc(
  localDate: string,
  timeZone: string = DEFAULT_FAMILY_TIMEZONE,
): Date {
  const start = localDayStartUtc(localDate, timeZone);
  return new Date(start.getTime() + DAY_MS);
}

/** 取家庭时区下的"今天" 'YYYY-MM-DD'。 */
export function todayLocal(now: Date = new Date(), timeZone: string = DEFAULT_FAMILY_TIMEZONE): string {
  return toLocalDateString(now, timeZone);
}

/** 'YYYY-MM-DD' 加减天数，返回同格式字符串。 */
export function addDays(localDate: string, days: number): string {
  const { y, m, d } = parseLocalDate(localDate);
  const base = new Date(Date.UTC(y, m - 1, d));
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

/** 两个日期字符串相差的天数（b - a）。 */
export function diffDays(a: string, b: string): number {
  const pa = Date.parse(`${a}T00:00:00Z`);
  const pb = Date.parse(`${b}T00:00:00Z`);
  return Math.round((pb - pa) / DAY_MS);
}

/** 日期字符串比较：a < b 返回负数。 */
export function compareDate(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * 把「周期长度」折算为截止日期（见方案 1.3 ⑪）。
 * 语义：从 startDate 起算，经过 years 年 months 月 days 天。
 * 采用日历加法（1 月 31 日 + 1 月 = 2 月末），与家长直觉一致。
 */
export function addPeriod(
  startDate: string,
  period: { years?: number; months?: number; days?: number },
): string {
  const { y, m, d } = parseLocalDate(startDate);
  const target = new Date(Date.UTC(y + (period.years ?? 0), m - 1 + (period.months ?? 0), 1));
  // 处理"月份溢出导致日期不存在"的情况：取该月最后一天
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  if (period.days) target.setUTCDate(target.getUTCDate() + period.days);
  return target.toISOString().slice(0, 10);
}

/** 取某天是星期几（0=周日 … 6=周六）。 */
export function weekdayOf(localDate: string): number {
  return new Date(`${localDate}T00:00:00Z`).getUTCDay();
}

/** 取某天的日号（1..31）。 */
export function dayOfMonth(localDate: string): number {
  return Number(localDate.slice(8, 10));
}

/**
 * 该月最后一天。
 *
 * 注意 `Date.UTC` 的 month 是 0-indexed：要取「第 m 月的最后一天」，
 * 需要传 `Date.UTC(y, m, 0)`（即第 m+1 月的第 0 天 = 第 m 月最后一天）。
 * 若传成 `Date.UTC(y, m - 1, 0)` 会得到上一个月，导致「每月最后一天」这类
 * 打卡规则算错一天的边界（进而影响统计分母）。
 */
export function lastDayOfMonth(localDate: string): number {
  const { y, m } = parseLocalDate(localDate);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** 人类可读的日期区间。 */
export function formatDateRange(
  startDate: string,
  endDate: string | null,
  isLongTerm = false,
): string {
  if (isLongTerm || !endDate) return `${startDate} 起 · 长期`;
  if (startDate === endDate) return startDate;
  return `${startDate} ~ ${endDate}`;
}

/**
 * ⚠️ 「实际用时」的格式化**不在这里** —— 它已经收敛到同目录的 `duration.ts`
 * （`formatDuration`）：全项目只有那一处口径，因为它会决定"45 秒"到底是写成
 * `'45 秒'` 还是被四舍五入成 `'1 分钟'`/`'0 分钟'`。本文件只管日期与时区。
 */
